import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getZone } from '@bratva/content';
import { spawnCars, walkMinutes } from '@bratva/rules';
import { canAct } from '@bratva/state';
import { Scene, type SceneTarget } from './scene/Scene.js';
import { store } from './game/store.js';
import { patrolsNow } from './game/heat.js';
import { travelFactor, walkFactor } from './game/character.js';
import { Hud } from './ui/Hud.js';
import { DeedPanel } from './ui/DeedPanel.js';
import { ShopPanel } from './ui/ShopPanel.js';
import { CharacterPanel } from './ui/CharacterPanel.js';
import { GaragePanel } from './ui/GaragePanel.js';
import { QuestPanel } from './ui/QuestPanel.js';
import { RunPanel } from './ui/RunPanel.js';
import { GameOver } from './ui/GameOver.js';
import { Tutorial } from './ui/Tutorial.js';

export function App(): JSX.Element {
  const save = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene | null>(null);
  const [target, setTarget] = useState<SceneTarget | null>(null);
  const [panel, setPanel] = useState<'shop' | 'garage' | 'character' | 'quests' | 'runs' | null>(null);
  const zone = getZone(save.player.zone);

  // Мир не хранится в сейве, а выводится из сида и дня — тот же сид даёт тот же двор.
  const cars = useMemo(
    () => spawnCars(save.seed, save.day, zone.id, zone.carSpots),
    [save.seed, save.day, zone.id, zone.carSpots],
  );

  // Сцена живёт вне React: пересоздаём её только при смене зоны.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new Scene(host, zone, {
      onWalk: (to, pathCost) =>
        store.dispatch({
          type: 'walk',
          to,
          minutes: walkMinutes(pathCost, { gearFactor: walkFactor(store.getSnapshot()) }),
        }),
      onExit: (exit) =>
        store.dispatch({
          type: 'travel',
          zone: exit.to,
          to: exit.arrive,
          // Дорога на тачке короче: множитель берётся из активной машины.
          minutes: Math.max(1, Math.round(exit.minutes * travelFactor(store.getSnapshot()))),
          label: exit.label,
        }),
      onReach: (reached) => setTarget(reached),
      canWalk: () => canAct(store.getSnapshot()),
    });
    sceneRef.current = scene;
    void scene.init(store.getSnapshot().player.tile);

    return () => {
      sceneRef.current = null;
      scene.destroy();
    };
  }, [zone]);

  useEffect(() => {
    sceneRef.current?.renderCars(cars);
  }, [cars]);

  // Патрули — функция минуты: сдвинулось время, сдвинулись и они.
  useEffect(() => {
    sceneRef.current?.renderPatrols(patrolsNow(save));
  }, [save]);

  // Ушёл в другую зону или лёг спать — панель дел закрывается сама.
  useEffect(() => {
    setTarget(null);
  }, [zone.id, save.day]);

  const closePanel = useCallback(() => setTarget(null), []);
  const closeOverlay = useCallback(() => setPanel(null), []);

  // Новый день или загруженное дело — ставим пацана туда, где он по документу.
  useEffect(() => {
    sceneRef.current?.setTile(store.getSnapshot().player.tile);
  }, [save.day, save.seed]);

  return (
    <div className="app">
      <div className="stage" ref={hostRef} />
      <Hud
        save={save}
        zone={zone}
        onOpenCharacter={() => setPanel('character')}
        onOpenQuests={() => setPanel('quests')}
        onOpenRuns={() => setPanel('runs')}
      />
      {target && (
        <DeedPanel
          target={target}
          save={save}
          onClose={closePanel}
          onOpenPanel={(which) => setPanel(which)}
        />
      )}
      {panel === 'shop' && <ShopPanel save={save} onClose={closeOverlay} />}
      {panel === 'garage' && <GaragePanel save={save} onClose={closeOverlay} />}
      {panel === 'character' && <CharacterPanel save={save} onClose={closeOverlay} />}
      {panel === 'quests' && <QuestPanel save={save} onClose={closeOverlay} />}
      {panel === 'runs' && <RunPanel save={save} onClose={closeOverlay} />}
      <Tutorial save={save} />
      <GameOver save={save} />
    </div>
  );
}
